import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useJobStore } from '@core/job/useJobStore';
import { useDaySequenceStore } from '@core/daySequence/useDaySequenceStore';
import { useClientStore } from '@core/client/useClientStore';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import { Job, Maybe } from '@core/generated/gql-types';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { useTenantId } from '@/contexts/TenantContext';
import { useUiFiltersStore } from '@/stores/useUiFiltersStore';
import { useUserStore } from '@core/user/useUserStore';
import {
  ACTIVE_ON_QUERY_PARAM,
  doesJobWorkInDateRange,
} from '@/modules/jobs/utils/jobFilters.utils';
import type { JobsListView } from '@/modules/jobs/views/jobsList.view';

const JOBS_PER_PAGE = 500;

const jobsDateRangeFetchOptions = (suppressGlobalLoading?: boolean) => ({
  includeApplicationCounts: true as const,
  ...(suppressGlobalLoading ? { suppressGlobalLoading: true as const } : {}),
});

const buildDefaultJobsRange = () => ({
  startIso: dayjs().startOf('day').toISOString(),
  endIso: dayjs().add(28, 'day').endOf('day').toISOString(),
});

export const useJobsListData = (view: JobsListView) => {
  const {
    jobs,
    fetchAllJobsForTenantByDateRange,
    fetchAllJobsForClientByDateRange,
    isLoading: jobsLoading,
  } = useJobStore();
  const tenantId = useTenantId();
  const { currentUser } = useUserStore();
  const clientId = currentUser?.clientId ?? undefined;
  const { fetchWorkersForTenant, fetchWorkersForClient } = useWorkerStore();
  const { fetchClientsForTenant } = useClientStore();
  const { fetchClassificationGroups } = useClassificationStore();
  const { fetchDaySequencesForJobIds } = useDaySequenceStore((state) => state);

  const jobsDateRange = useUiFiltersStore((state) => state.jobsDateRange);
  const setJobsDateRange = useUiFiltersStore((state) => state.setJobsDateRange);
  const [searchParams] = useSearchParams();
  const activeOn = searchParams.get(ACTIVE_ON_QUERY_PARAM);
  const fallbackRangeRef = useRef(buildDefaultJobsRange());

  const [isLoading, setIsLoading] = useState(false);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | null>(null);
  const [hasMoreFromServer, setHasMoreFromServer] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const hasFetchedInitial = useRef(false);
  const prefetchSessionRef = useRef(0);
  const daySequencesFetchedForJobIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!jobsDateRange) {
      setJobsDateRange(fallbackRangeRef.current);
    }
  }, [jobsDateRange, setJobsDateRange]);

  const activeStartIso =
    jobsDateRange?.startIso ?? fallbackRangeRef.current.startIso;
  const activeEndIso = jobsDateRange?.endIso ?? fallbackRangeRef.current.endIso;

  const dateRange = useMemo<{ start: Date; end: Date }>(() => {
    const activeDay = activeOn ? dayjs(activeOn) : null;
    if (activeDay?.isValid()) {
      return {
        start: activeDay.startOf('day').toDate(),
        end: activeDay.endOf('day').toDate(),
      };
    }

    return {
      start: new Date(activeStartIso),
      end: new Date(activeEndIso),
    };
  }, [activeOn, activeStartIso, activeEndIso]);

  const jobsForRole = useMemo(() => jobs ?? [], [jobs]);

  const jobsInSelectedDateRange = useMemo(
    () =>
      jobsForRole.filter((job) =>
        doesJobWorkInDateRange(job, dateRange.start, dateRange.end),
      ),
    [jobsForRole, dateRange.start, dateRange.end],
  );

  const enqueueDaySequencesForJobs = useCallback(
    (
      jobsFromResponse: Array<Maybe<Job>>,
      options?: { prefetchSessionId?: number },
    ) => {
      if (
        options?.prefetchSessionId != null &&
        options.prefetchSessionId !== prefetchSessionRef.current
      ) {
        return;
      }

      const newJobIds = jobsFromResponse
        .map((job) => job?.id)
        .filter((jobId: string | undefined) => jobId != undefined)
        .filter((jobId) => !daySequencesFetchedForJobIdsRef.current.has(jobId));

      if (newJobIds.length === 0) {
        return;
      }

      newJobIds.forEach((jobId) =>
        daySequencesFetchedForJobIdsRef.current.add(jobId),
      );

      void fetchDaySequencesForJobIds(newJobIds);
    },
    [fetchDaySequencesForJobIds],
  );

  const prefetchRemainingClientJobsForRange = useCallback(
    async (
      range: { start: Date; end: Date },
      initialLastKey: string,
      prefetchSessionId: number,
    ) => {
      if (!clientId) return;

      const dateFrom = dayjs(range.start).startOf('day').toISOString();
      const dateTo = dayjs(range.end).endOf('day').toISOString();
      let nextKey: string | null = initialLastKey;

      while (
        nextKey &&
        prefetchSessionId === prefetchSessionRef.current &&
        clientId
      ) {
        const jobsResponse = await fetchAllJobsForClientByDateRange(
          clientId,
          dateFrom,
          dateTo,
          undefined,
          JOBS_PER_PAGE,
          nextKey,
          { suppressGlobalLoading: true },
        );

        if (prefetchSessionId !== prefetchSessionRef.current) {
          return;
        }

        enqueueDaySequencesForJobs(jobsResponse.jobs, { prefetchSessionId });
        setLastEvaluatedKey(jobsResponse.lastEvaluatedKey ?? null);
        setHasMoreFromServer(jobsResponse.hasMore);

        if (!jobsResponse.hasMore || !jobsResponse.lastEvaluatedKey) {
          break;
        }
        nextKey = jobsResponse.lastEvaluatedKey;
      }
    },
    [clientId, fetchAllJobsForClientByDateRange, enqueueDaySequencesForJobs],
  );

  const fetchClientJobsByDateRange = useCallback(
    async (
      range: { start: Date; end: Date },
      nextKey?: string | null,
      prefetchSessionId?: number,
    ) => {
      if (!clientId) return;

      const isInitial = nextKey == null;
      if (isInitial) setIsLoading(true);
      else setIsFetchingMore(true);

      try {
        const dateFrom = dayjs(range.start).startOf('day').toISOString();
        const dateTo = dayjs(range.end).endOf('day').toISOString();

        const jobsResponse = await fetchAllJobsForClientByDateRange(
          clientId,
          dateFrom,
          dateTo,
          undefined,
          JOBS_PER_PAGE,
          nextKey ?? undefined,
        );

        if (
          prefetchSessionId != null &&
          prefetchSessionId !== prefetchSessionRef.current
        ) {
          return;
        }

        enqueueDaySequencesForJobs(jobsResponse.jobs, {
          prefetchSessionId: prefetchSessionId ?? undefined,
        });

        setLastEvaluatedKey(jobsResponse.lastEvaluatedKey ?? null);
        setHasMoreFromServer(jobsResponse.hasMore);
        if (isInitial) hasFetchedInitial.current = true;

        if (
          isInitial &&
          prefetchSessionId != null &&
          jobsResponse.hasMore &&
          jobsResponse.lastEvaluatedKey
        ) {
          void prefetchRemainingClientJobsForRange(
            range,
            jobsResponse.lastEvaluatedKey,
            prefetchSessionId,
          );
        }
      } finally {
        if (
          prefetchSessionId == null ||
          prefetchSessionId === prefetchSessionRef.current
        ) {
          setIsLoading(false);
          setIsFetchingMore(false);
        }
      }
    },
    [
      clientId,
      fetchAllJobsForClientByDateRange,
      prefetchRemainingClientJobsForRange,
      enqueueDaySequencesForJobs,
    ],
  );

  const prefetchRemainingJobsForRange = useCallback(
    async (
      range: { start: Date; end: Date },
      initialLastKey: string,
      prefetchSessionId: number,
    ) => {
      if (!tenantId) return;

      const dateFrom = dayjs(range.start).startOf('day').toISOString();
      const dateTo = dayjs(range.end).endOf('day').toISOString();
      let nextKey: string | null = initialLastKey;

      while (
        nextKey &&
        prefetchSessionId === prefetchSessionRef.current &&
        tenantId
      ) {
        const jobsResponse = await fetchAllJobsForTenantByDateRange(
          tenantId,
          dateFrom,
          dateTo,
          undefined,
          JOBS_PER_PAGE,
          nextKey,
          jobsDateRangeFetchOptions(true),
        );

        if (prefetchSessionId !== prefetchSessionRef.current) {
          return;
        }

        enqueueDaySequencesForJobs(jobsResponse.jobs, { prefetchSessionId });
        setLastEvaluatedKey(jobsResponse.lastEvaluatedKey ?? null);
        setHasMoreFromServer(jobsResponse.hasMore);

        if (!jobsResponse.hasMore || !jobsResponse.lastEvaluatedKey) {
          break;
        }
        nextKey = jobsResponse.lastEvaluatedKey;
      }
    },
    [tenantId, fetchAllJobsForTenantByDateRange, enqueueDaySequencesForJobs],
  );

  const fetchJobsByDateRange = useCallback(
    async (
      range: { start: Date; end: Date },
      nextKey?: string | null,
      prefetchSessionId?: number,
    ) => {
      if (!tenantId) return;

      const isInitial = nextKey == null;
      if (isInitial) setIsLoading(true);
      else setIsFetchingMore(true);

      try {
        const dateFrom = dayjs(range.start).startOf('day').toISOString();
        const dateTo = dayjs(range.end).endOf('day').toISOString();

        const jobsResponse = await fetchAllJobsForTenantByDateRange(
          tenantId,
          dateFrom,
          dateTo,
          undefined,
          JOBS_PER_PAGE,
          nextKey ?? undefined,
          jobsDateRangeFetchOptions(),
        );

        if (
          prefetchSessionId != null &&
          prefetchSessionId !== prefetchSessionRef.current
        ) {
          return;
        }

        enqueueDaySequencesForJobs(jobsResponse.jobs, {
          prefetchSessionId: prefetchSessionId ?? undefined,
        });

        setLastEvaluatedKey(jobsResponse.lastEvaluatedKey ?? null);
        setHasMoreFromServer(jobsResponse.hasMore);
        if (isInitial) hasFetchedInitial.current = true;

        if (
          isInitial &&
          prefetchSessionId != null &&
          jobsResponse.hasMore &&
          jobsResponse.lastEvaluatedKey
        ) {
          void prefetchRemainingJobsForRange(
            range,
            jobsResponse.lastEvaluatedKey,
            prefetchSessionId,
          );
        }
      } finally {
        if (
          prefetchSessionId == null ||
          prefetchSessionId === prefetchSessionRef.current
        ) {
          setIsLoading(false);
          setIsFetchingMore(false);
        }
      }
    },
    [
      tenantId,
      fetchAllJobsForTenantByDateRange,
      prefetchRemainingJobsForRange,
      enqueueDaySequencesForJobs,
    ],
  );

  useEffect(() => {
    if (view.query.jobs.strategy !== 'clientByDateRange') return;
    if (!clientId) return;

    prefetchSessionRef.current += 1;
    const prefetchSessionId = prefetchSessionRef.current;
    hasFetchedInitial.current = false;
    setLastEvaluatedKey(null);
    setHasMoreFromServer(true);
    void fetchClientJobsByDateRange(dateRange, null, prefetchSessionId);
  }, [
    view.query.jobs.strategy,
    clientId,
    dateRange,
    fetchClientJobsByDateRange,
  ]);

  useEffect(() => {
    if (view.query.jobs.strategy !== 'tenantByDateRange') return;
    if (!tenantId) return;

    prefetchSessionRef.current += 1;
    const prefetchSessionId = prefetchSessionRef.current;
    hasFetchedInitial.current = false;
    void fetchJobsByDateRange(dateRange, null, prefetchSessionId);
  }, [
    view.query.jobs.strategy,
    tenantId,
    dateRange,
    fetchJobsByDateRange,
  ]);

  useEffect(() => {
    if (!tenantId) return;
    if (view.query.clients.fetch) {
      void fetchClientsForTenant(tenantId, undefined, undefined, true);
    }
    if (view.query.classifications.fetch) {
      void fetchClassificationGroups(tenantId);
    }
  }, [
    tenantId,
    view.query.clients.fetch,
    view.query.classifications.fetch,
    fetchClientsForTenant,
    fetchClassificationGroups,
  ]);

  useEffect(() => {
    switch (view.query.workers.strategy) {
      case 'tenant':
        if (tenantId) {
          void fetchWorkersForTenant(tenantId);
        }
        break;
      case 'client':
        if (clientId) {
          void fetchWorkersForClient(clientId);
        }
        break;
      default:
        break;
    }
  }, [
    view.query.workers.strategy,
    tenantId,
    clientId,
    fetchWorkersForTenant,
    fetchWorkersForClient,
  ]);

  const commitDateRange = useCallback(
    (range: { start: Date; end: Date }) => {
      if (
        view.query.jobs.strategy === 'tenantByDateRange' ||
        view.query.jobs.strategy === 'clientByDateRange'
      ) {
        prefetchSessionRef.current += 1;
        setLastEvaluatedKey(null);
        setHasMoreFromServer(true);
        hasFetchedInitial.current = false;
      }
      setJobsDateRange({
        startIso: dayjs(range.start).startOf('day').toISOString(),
        endIso: dayjs(range.end).endOf('day').toISOString(),
      });
    },
    [setJobsDateRange, view.query.jobs.strategy],
  );

  const fetchNextPage = useCallback(async () => {
    if (!hasMoreFromServer || isFetchingMore || !lastEvaluatedKey) return;

    setIsFetchingMore(true);
    const prefetchSessionWhenStarted = prefetchSessionRef.current;

    try {
      if (view.query.jobs.strategy === 'clientByDateRange') {
        if (!clientId) return;

        const jobsResponse = await fetchAllJobsForClientByDateRange(
          clientId,
          dayjs(dateRange.start).startOf('day').toISOString(),
          dayjs(dateRange.end).endOf('day').toISOString(),
          undefined,
          JOBS_PER_PAGE,
          lastEvaluatedKey,
          { suppressGlobalLoading: true },
        );

        if (prefetchSessionWhenStarted !== prefetchSessionRef.current) {
          return;
        }

        enqueueDaySequencesForJobs(jobsResponse.jobs);
        setLastEvaluatedKey(jobsResponse.lastEvaluatedKey ?? null);
        setHasMoreFromServer(jobsResponse.hasMore);
        return;
      }

      if (!tenantId) return;

      const jobsResponse = await fetchAllJobsForTenantByDateRange(
        tenantId,
        dayjs(dateRange.start).startOf('day').toISOString(),
        dayjs(dateRange.end).endOf('day').toISOString(),
        undefined,
        JOBS_PER_PAGE,
        lastEvaluatedKey,
        jobsDateRangeFetchOptions(true),
      );

      if (prefetchSessionWhenStarted !== prefetchSessionRef.current) {
        return;
      }

      enqueueDaySequencesForJobs(jobsResponse.jobs);
      setLastEvaluatedKey(jobsResponse.lastEvaluatedKey ?? null);
      setHasMoreFromServer(jobsResponse.hasMore);
    } finally {
      setIsFetchingMore(false);
    }
  }, [
    view.query.jobs.strategy,
    clientId,
    tenantId,
    dateRange,
    hasMoreFromServer,
    isFetchingMore,
    lastEvaluatedKey,
    fetchAllJobsForClientByDateRange,
    fetchAllJobsForTenantByDateRange,
    enqueueDaySequencesForJobs,
  ]);

  return {
    clientId,
    dateRange,
    commitDateRange,
    jobsForRole,
    jobsInSelectedDateRange,
    isLoading,
    isFetchingMore,
    jobsLoading,
    hasMoreFromServer,
    fetchNextPage,
  };
};
